import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import * as moment from 'moment';
import Decimal from 'decimal.js';
import { AccommodationType } from '../accommodation-type';
import { BoardType } from '../board-type';
import { BookingPost } from '../booking-post';
import { Country } from '../country';
import { Currency } from '../currency';
import { Gender } from '../gender';
import { GroupBookingPost } from '../group-booking-post';
import { HttpService } from '../service/http.service';
import { Language } from '../language';
import { RoomType } from '../room-type';
import { SalesAgent } from '../sales-agent';
import { SimpleSchool } from '../simple-school';
import { StudentPost } from '../student-post';
import { ToastMessageService } from '../toast-message.service';
import { GroupBookingGuidePost } from '../group-booking-guide-post';
import { PhoneNumberValidator } from '../validator/phone-number-validator';
import { InsuranceSupplier } from '../insurance-supplier';
import { StudentRoom } from '../student-room';
import { LanguageCourse } from '../language-course';
import { GroupBookingCsvItem } from '../group-booking-csv-item';
import { GroupBookingForm } from '../form-template/group-booking-form';
import { GroupBookingGuideForm } from '../form-template/group-booking-guide-form';
import { GroupBookingBookingForm } from '../form-template/group-booking-booking-form';
import { GroupBookingHeadersForm } from '../form-template/group-booking-headers-form';
import { CsvHeader } from '../csv-header';

@Component({
  selector: 'app-group-booking-create',
  templateUrl: './group-booking-create.component.html',
  styleUrls: ['./group-booking-create.component.css']
})
export class GroupBookingCreateComponent implements OnInit {

  groupBookingForm: FormGroup<GroupBookingForm>;
  csvHeadersForm: FormGroup<GroupBookingHeadersForm>;
  genders: Gender[] = [];
  countries: Country[] = [];
  schools: SimpleSchool[] = [];
  currencies: Currency[] = [];
  agents: SalesAgent[] = [];
  languages: Language[] = [];
  accommodationTypes: AccommodationType[] = [];
  roomTypes: RoomType[] = [];
  boardTypes: BoardType[] = [];
  insuranceSuppliers: InsuranceSupplier[] = [];
  roomNames: StudentRoom[] = [];
  languageCourseList: LanguageCourse[] = [];
  studentGenderList: (Gender | null)[] = [];
  isMappingHeader = false;
  csvHeaders: CsvHeader[] = [];

  defaultBirthDate: Date = moment().subtract(20, "years").toDate();
  birthYearRange = moment().subtract(30, "years").year() + ":" + moment().year();

  postingGroupBooking = false;
  csvNullHeader: CsvHeader = { label: "NONE", index: -1 };
  bookingItems: GroupBookingCsvItem[] = [];

  csvStr: string = "";

  constructor(
    private httpService: HttpService,
    private toastService: ToastMessageService,
    private builder: FormBuilder
  ) {
    let bookingGuideForms: FormGroup<GroupBookingGuideForm>[] = [];
    let bookingBookingForms: FormGroup<GroupBookingBookingForm>[] = [];
    this.groupBookingForm = this.builder.group({
      global: this.builder.group({
        destination: new FormControl<Country | null>(null, Validators.required),
        school: new FormControl<SimpleSchool | null>(null, Validators.required),
        language: new FormControl<Language | null>(null, Validators.required),
        startDate: new FormControl<Date | null>(null),
        endDate: new FormControl<Date | null>(null),
        salesAgent: new FormControl<SalesAgent | null>(null, Validators.required),
        formAddNumber: new FormControl<number | null>(null),
        groupName: new FormControl<string | null>(null, Validators.required),
        accommodationType: new FormControl<AccommodationType | null>(null, Validators.required),
        roomType: new FormControl<RoomType | null>(null, Validators.required),
        boardType: new FormControl<BoardType | null>(null, Validators.required),
        totalAmount: new FormControl<number | null>(null, Validators.required),
        manualAmountEncoding: new FormControl<boolean>(false, { nonNullable: true }),
        deposit: new FormControl<number | null>(0, Validators.required),
        dueDate: new FormControl<Date | null>(null, Validators.required),
        insurance: new FormControl<InsuranceSupplier | null>(null)
      }),
      bookings: this.builder.array(bookingBookingForms),
      guides: this.builder.array(bookingGuideForms)
    });
    this.csvHeadersForm = this.builder.group({
      firstName: new FormControl(this.getHeaderBylabel("first_name"), { nonNullable: true }),
      lastName: new FormControl(this.getHeaderBylabel("last_name"), { nonNullable: true }),
      gender: new FormControl(this.getHeaderBylabel("gender"), { nonNullable: true }),
      startDate: new FormControl(this.getHeaderBylabel("start_date"), { nonNullable: true }),
      endDate: new FormControl(this.getHeaderBylabel("end_name"), { nonNullable: true }),
      birthDate: new FormControl(this.getHeaderBylabel("birth_date"), { nonNullable: true }),
      allergies: new FormControl(this.getHeaderBylabel("allergies"), { nonNullable: true }),
      insurance: new FormControl(this.getHeaderBylabel("insurance"), { nonNullable: true })
    });
  }

  getHeaderBylabel(label: string) {
    for (let header of this.csvHeaders) {
      if (header.label === label) {
        return header;
      }
    }
    return this.csvNullHeader;
  }

  ngOnInit(): void {
    this.httpService.getGenders().subscribe(res => {
      this.genders = res;
      for (let gender of this.genders) {
        if (gender.id === 3) {
          gender.symbol = "X";
        }
      }
    });
    this.httpService.getCurrencies().subscribe(res => {
      this.currencies = res;
    });
    this.httpService.getAllCountries().subscribe(res => {
      this.countries = res;
    });
    this.httpService.getSimpleSchools().subscribe(res => {
      this.schools = res;
    });
    this.httpService.getSalesAgents().subscribe(res => {
      this.agents = res;
      this.bookingGlobalForm.get("salesAgent")!.setValue(this.agents[0]);
    });
    this.httpService.getLanguages().subscribe(res => {
      this.languages = res;
    });
    this.httpService.getAccommodationTypes().subscribe(res => {
      this.accommodationTypes = res;
      this.bookingGlobalForm.get("accommodationType")!.setValue(this.accommodationTypes[0]);
    });
    this.httpService.getRoomTypes().subscribe(res => {
      this.roomTypes = res;
      this.bookingGlobalForm.get("roomType")!.setValue(this.roomTypes[0]);
    });
    this.httpService.getBoardTypes().subscribe(res => {
      this.boardTypes = res;
      this.bookingGlobalForm.get("boardType")!.setValue(this.boardTypes[0]);
    });
    this.httpService.getTravelInsuranceSuppliers().subscribe(res => {
      this.insuranceSuppliers = res;
    });
  }

  addBookingFormsWithValue(bookingItems: GroupBookingCsvItem[]) {
    for (let item of bookingItems) {
      let selectedGender: Gender | null = null;

      for (let gender of this.genders) {
        if (item.gender && item.gender.length > 0 && gender.symbol.toLowerCase() == item.gender.trim().toLowerCase()) {
          selectedGender = gender;
        }
      }
      let group = this.builder.group({
        lastName: [item.lastName, Validators.required],
        firstName: [item.firstName, Validators.required],
        startDate: [item.startDate ? item.startDate : null, Validators.required],
        endDate: [item.endDate ? item.endDate : null, Validators.required],
        gender: selectedGender,
        accommodationType: [this.bookingGlobalForm.value.accommodationType, Validators.required],
        roomType: [this.bookingGlobalForm.value.roomType, Validators.required],
        boardType: [this.bookingGlobalForm.value.boardType, Validators.required],
        dueAmount: [0, Validators.required],
        allergies: [item.allergies],
        birthDate: [item.birthDate, Validators.required],
        studentRoom: null,
        insuranceSupplier: this.bookingGlobalForm.value.insurance,
        languageCourse: [null, Validators.required]
      });
      this.bookingFormArray.push(group);
    }
  }

  addBookingForms() {
    let formAddNumber = this.bookingGlobalForm.value.formAddNumber;

    if (!formAddNumber) {
      this.toastService.addError("Error", "Please enter the number of bookings you want to create");
      return;
    }
    if (Number.isNaN(Number(formAddNumber))) {
      this.toastService.addError("Error", "the number of booking you want to create should be a number");
      return;
    }
    if (formAddNumber > 99) {
      formAddNumber = 99;
    }
    let defaultDueAmount = new Decimal(0);

    for (let i = 0; i < formAddNumber; i++) {
      let group = this.builder.group({
        lastName: [null, Validators.required],
        firstName: [null, Validators.required],
        startDate: [this.bookingGlobalForm.value.startDate ? this.bookingGlobalForm.value.startDate : null, Validators.required],
        endDate: [this.bookingGlobalForm.value.endDate ? this.bookingGlobalForm.value.endDate : null, Validators.required],
        gender: null,
        accommodationType: [this.bookingGlobalForm.value.accommodationType, Validators.required],
        roomType: [this.bookingGlobalForm.value.roomType, Validators.required],
        boardType: [this.bookingGlobalForm.value.boardType, Validators.required],
        dueAmount: [defaultDueAmount, Validators.required],
        allergies: null,
        birthDate: [null, Validators.required],
        studentRoom: null,
        insuranceSupplier: this.bookingGlobalForm.value.insurance,
        languageCourse: [null, Validators.required]
      });
      this.bookingFormArray.push(group);
    }
    this.roomNames = [];
    for (let j = 0; j < this.bookingFormArray.controls.length; j++) {
      this.addRoomName(j);
    }
    if (!this.bookingGlobalForm.value.manualAmountEncoding) {
      this.recalculateIndividualDueAmount();
    }
  }

  initHeadersForm() {
    this.csvHeadersForm = this.builder.group({
      firstName: new FormControl(this.getHeaderBylabel("first_name"), { nonNullable: true }),
      lastName: new FormControl(this.getHeaderBylabel("last_name"), { nonNullable: true }),
      gender: new FormControl(this.getHeaderBylabel("gender"), { nonNullable: true }),
      startDate: new FormControl(this.getHeaderBylabel("start_date"), { nonNullable: true }),
      endDate: new FormControl(this.getHeaderBylabel("end_name"), { nonNullable: true }),
      birthDate: new FormControl(this.getHeaderBylabel("birth_date"), { nonNullable: true }),
      allergies: new FormControl(this.getHeaderBylabel("allergies"), { nonNullable: true }),
      insurance: new FormControl(this.getHeaderBylabel("insurance"), { nonNullable: true })
    });
  }

  onFileSelect(files: File[]) {
    if (files && files.length > 0) {
      let file = files[0];
      let reader = new FileReader();
      reader.readAsText(file);
      reader.onload = (e) => {
        let str = reader.result as string;
        this.csvStr = str;
        this.isMappingHeader = true;
        this.parseCSV(str);
      }
    }
  }

  addBookingsByCsv() {
    const lines = this.csvStr.trim().split('\n');
    const items: GroupBookingCsvItem[] = [];
    let lastNameIndex = this.csvHeadersForm.value.lastName?.index;
    let firstNameIndex = this.csvHeadersForm.value.firstName?.index;
    let genderIndex = this.csvHeadersForm.value.gender?.index;
    let startDateIndex = this.csvHeadersForm.value.startDate?.index;
    let endDateIndex = this.csvHeadersForm.value.endDate?.index;
    let birthDateIndex = this.csvHeadersForm.value.birthDate?.index;
    let allergiesIndex = this.csvHeadersForm.value.allergies?.index;
    let insuranceIndex = this.csvHeadersForm.value.insurance?.index;

    for (let i = 1; i < lines.length; i++) {
      const row = lines[i].split(',');
      const item: GroupBookingCsvItem = {
        lastName: lastNameIndex != -1 ? row[lastNameIndex!].trim() : "",
        firstName: firstNameIndex != -1 ? row[firstNameIndex!].trim() : "",
        gender: genderIndex != -1 ? row[genderIndex!].trim() : "",
        startDate: startDateIndex != -1 ? row[startDateIndex!].trim() : "",
        endDate: endDateIndex != -1 ? row[endDateIndex!].trim() : "",
        course: "",
        birthDate: birthDateIndex != -1 ? row[birthDateIndex!].trim() : "",
        allergies: allergiesIndex != -1 ? row[allergiesIndex!].trim() : "",
        insurance: insuranceIndex != -1 ? row[insuranceIndex!].trim() : ""
      };
      items.push(item);
    }
    this.addBookingFormsWithValue(items);
    this.isMappingHeader = false;
  }

  parseCSV(dataStr: string): void {
    const lines = dataStr.trim().split('\n');
    const headers = lines[0].split(',');
    this.csvHeaders = [];
    this.csvHeaders.push(this.csvNullHeader);
    for (let i = 0; i < headers.length; i++) {
      let label = headers[i].replace("\r", "");
      this.csvHeaders.push({ label: label, index: i });
    }
    this.initHeadersForm();
  }

  addRoomName(i: number) {
    let roomNumber = i + 1;
    this.roomNames.push(new StudentRoom(roomNumber, "Room " + roomNumber));
  }

  removeRoomName() {
    this.roomNames.pop();
    let newRoomArray = [];
    for (let room of this.roomNames) {
      newRoomArray.push(room);
    }
    this.roomNames = newRoomArray;
  }

  insertGuideForm() {
    this.guideFormArray.push(this.builder.group({
      firstName: [null, Validators.required],
      lastName: [null, Validators.required],
      address: null,
      phoneNumber: [null, PhoneNumberValidator],
      email: null
    }));
  }

  recalculateIndividualDueAmount() {
    let totalDueAmount = new Decimal(this.bookingGlobalForm.value.totalAmount);
    let individualDueAmount = totalDueAmount.div(this.bookingFormArray.controls.length);
    for (let form of this.bookingFormArray.controls) {
      form.get("dueAmount")!.setValue(individualDueAmount);
    }
  }

  get bookingFormArray(): FormArray {
    return this.groupBookingForm.get("bookings") as FormArray;
  }

  get bookingGlobalForm(): FormGroup {
    return this.groupBookingForm.get("global") as FormGroup;
  }
  get guideFormArray(): FormArray {
    return this.groupBookingForm.get("guides") as FormArray;
  }

  removeBookingFormItem(index: number) {
    this.studentGenderList.splice(index, 1);
    this.bookingFormArray.removeAt(index);
    if (!this.bookingGlobalForm.value.manualAmountEncoding) {
      this.recalculateIndividualDueAmount();
    }
    this.removeRoomName();
  }

  removeGuideFormItem(index: number) {
    this.guideFormArray.removeAt(index);
  }

  postGroupBooking() {
    if (this.checkTotalDueAmount() == false) {
      this.toastService.addError("Error", "Sum of amounts doesn't match to encoded total amount");
      return;
    }
    if (this.groupBookingForm.valid && !this.postingGroupBooking) {
      this.postingGroupBooking = true;
      let groupBooking = new GroupBookingPost(
        this.bookingGlobalForm.value.groupName,
        [],
        []
      );

      //create guides
      for (let form of this.guideFormArray.controls) {
        groupBooking.guides.push(new GroupBookingGuidePost(
          form.value.firstName,
          form.value.lastName,
          form.value.phoneNumber,
          form.value.email,
          form.value.address
        ));
      }

      //create bookings
      for (let form of this.bookingFormArray.controls) {
        groupBooking.bookings.push(new BookingPost(
          moment(form.value.startDate).utc(true),
          moment(form.value.endDate).utc(true),
          this.bookingGlobalForm.value.school.id,
          this.bookingGlobalForm.value.destination.id,
          new StudentPost(
            form.value.lastName,
            form.value.firstName,
            this.bookingGlobalForm.value.language.id,
            form.value.gender == null ? null : form.value.gender.id,
            form.value.studentRoom,
            form.value.allergies,
            form.value.birthDate == null ? null : moment(form.value.birthDate).utc(true)
          ),
          this.bookingGlobalForm.value.salesAgent.id,
          form.value.accommodationType.id,
          form.value.roomType.id,
          form.value.boardType.id,
          new Decimal(form.value.dueAmount),
          form.value.insuranceSupplier,
          this.bookingGlobalForm.value.deposit,
          this.bookingGlobalForm.value.dueDate == null ? null : moment(this.bookingGlobalForm.value).utc(true),
          null,
          form.value.languageCourse.id
        ));
      }

      this.httpService.postGroupBooking(groupBooking).subscribe(() => {
        this.toastService.addSuccess("Success", this.bookingFormArray.controls.length + " bookings are created");
        this.bookingFormArray.clear();
        this.groupBookingForm.reset();
        this.postingGroupBooking = false;
      }, error => {
        this.toastService.addError("Error", error.error.message);
        this.postingGroupBooking = false;
      });
    } else {
      this.toastService.addError("Error", "Form validation error");
    }
  }

  checkTotalDueAmount() {
    let totalDueAmount = new Decimal(0);
    for (let form of this.bookingFormArray.controls) {
      totalDueAmount = totalDueAmount.plus(form.value.dueAmount);
    }
    if (totalDueAmount.equals(this.bookingGlobalForm.value.totalAmount)) {
      return true;
    } else {
      return false;
    }
  }

  completeForm() {
    this.groupBookingForm.get("global")!.get("destination")!.setValue(this.countries[0]);
    this.groupBookingForm.get("global")!.get("school")!.setValue(this.schools[0]);
    this.groupBookingForm.get("global")!.get("language")!.setValue(this.languages[0]);
    this.groupBookingForm.get("global")!.get("startDate")!.setValue(new Date());
    this.groupBookingForm.get("global")!.get("endDate")!.setValue(moment().add(30, "days").toDate());
    this.groupBookingForm.get("global")!.get("groupName")!.setValue("Mock booking");
    this.groupBookingForm.get("global")!.get("formAddNumber")!.setValue(3);
    this.groupBookingForm.get("global")!.get("totalAmount")!.setValue(3000);
    this.groupBookingForm.get("global")!.get("dueDate")!.setValue(new Date());
  }

  onSchoolChange() {
    const schoolId = this.groupBookingForm.value.global!.school?.id;
    for (let form of this.bookingFormArray.controls) {
      form.get("languageCourse")!.setValue(null);
    }
    this.httpService.getLanguageCoursesBySchoolId(schoolId!).subscribe(res => {
      this.languageCourseList = res;
    });
  }

}